import {
  Scene,
  StereoCamera,
  PlaneGeometry,
  VideoTexture,
  MeshBasicMaterial,
  Mesh,
  Vector2,
  SRGBColorSpace,
  SphereGeometry,
  BackSide,
} from "three";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader";
import { getPerspectiveCamera, getWebGLRenderer } from "./viewHelper";

export default class View3d {
  constructor(props) {
    this.scene = null;
    this.renderer = null;
    this.screen = null;
    this.defaultCamera = null;
    this.stereoCamera = null;
    this.orbitControl = null;
    this.environmentSphere = null;
    this.props = props;
  }

  init() {
    if (this.props.streamThreading === "single-threaded") {
      const video = document.createElement("video");
      video.id = "srcVideo";
      video.setAttribute("crossorigin", "anonymous");
      video.hidden = false;
      video.setAttribute("playsInline", "true");
      video.setAttribute("muted", "true");
      video.controls = false;
      video.autoplay = true;
      document.getElementById("container").appendChild(video);
    }

    this.renderer = getWebGLRenderer(false);

    this.scene = new Scene();
    this.defaultCamera = getPerspectiveCamera();
    this.defaultCamera.position.z = 10;

    this.stereoCamera = new StereoCamera();
    this.stereoCamera.aspect = 0.5;
    this.stereoCamera.eyeSep = 1;
  }

  renderScreen({ stream }) {
    const screenGeometry = new PlaneGeometry(16, 9);
    let videoTexture = null;
    if (stream) {
      let srcVideo = null;
      if (this.props.streamThreading === "single-threaded") {
        srcVideo = document.getElementById("srcVideo");
        srcVideo.srcObject = stream;
      } else {
        const videoTrackId = stream.videoTrack._ID;
        stream.videoTrack.play("container");
        srcVideo = document.getElementById("video_" + videoTrackId);
      }
      videoTexture = new VideoTexture(srcVideo);
      videoTexture.colorSpace = SRGBColorSpace;
    }

    const videoMappedMaterial = new MeshBasicMaterial({ map: videoTexture });
    this.screen = new Mesh(screenGeometry, videoMappedMaterial);
    this.screen.position.set(-1, 1, -15);
  }

  loadTextureUsingSphere({ imagePath }) {
    const hdrTexture = new RGBELoader().load(imagePath);
    const sphereGeometry = new SphereGeometry(65, 40, 30);
    const sphereMaterial = new MeshBasicMaterial({
      map: hdrTexture,
    });

    sphereMaterial.side = BackSide;
    const sphereMesh = new Mesh(sphereGeometry, sphereMaterial);
    this.environmentSphere = sphereMesh;
    this.scene.add(this.environmentSphere);
  }

  runAnimator() {
    this.getDefaultCamera().updateWorldMatrix();
    this.stereoCamera.update(this.getDefaultCamera());

    this.getRenderer().render(this.getScene(), this.defaultCamera);

    const size = new Vector2();
    this.getRenderer().getSize(size);

    this.getRenderer().setScissorTest(true);
    this.getRenderer().setScissor(0, 0, size.width / 2, size.height);
    this.getRenderer().setViewport(0, 0, size.width / 2, size.height);
    this.getRenderer().render(this.getScene(), this.stereoCamera.cameraR);
    this.getRenderer().setScissor(
      size.width / 2,
      0,
      size.width / 2,
      size.height,
    );
    this.getRenderer().setViewport(
      size.width / 2,
      0,
      size.width / 2,
      size.height,
    );
    this.getRenderer().render(this.getScene(), this.stereoCamera.cameraL);
    this.getRenderer().setScissorTest(false);
  }

  getScene() {
    return this.scene;
  }

  getEnvironmentSphere() {
    return this.environmentSphere;
  }

  getRenderer() {
    return this.renderer;
  }

  getScreen() {
    return this.screen;
  }

  getDefaultCamera() {
    return this.defaultCamera;
  }

  getOrbitController() {
    return this.orbitControl;
  }
}
